import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CurationStatus, MediaPackageSource, StickerSource } from '../api';
import { animatedBadgeLabel, type StickerAnimationFacts } from '../stickerAnimation';

// Tinted-badge pattern per TfxStatusBadge: transparent border, translucent bg.
const base = 'border-transparent font-medium';

/** Retired release artwork stays available to drafts but is excluded from browsing. */
export function CurationStatusBadge({ status }: { status: CurationStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        base,
        status === 'ACTIVE'
          ? 'bg-emerald-400/15 text-emerald-400'
          : status === 'RETIRED'
            ? 'bg-zinc-400/15 text-zinc-400'
            : 'bg-red-400/15 text-red-400',
      )}
    >
      {status === 'ACTIVE' ? 'LIVE' : status}
    </Badge>
  );
}

const PACKAGE_SOURCE_LABEL: Record<MediaPackageSource, string> = {
  PUBLISHED: 'Reviewed',
  ADOPTED: 'Factory',
  FACTORY: 'Factory',
};

const PACKAGE_SOURCE_TITLE: Record<MediaPackageSource, string> = {
  PUBLISHED: 'Authored here and published through the draft → review chain',
  ADOPTED: 'Published by the offline factory, now curated here',
  FACTORY: 'Published by the offline factory; delisting adopts it for curation',
};

/**
 * Where a package came from. Reviewed packages went through this portal's
 * review gate; factory packages did not, and a factory publish run can restore
 * one after a delist — which is why the distinction is worth showing.
 */
export function PackageSourceBadge({ source }: { source: MediaPackageSource }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        base,
        source === 'PUBLISHED' ? 'bg-blue-400/15 text-blue-400' : 'bg-zinc-400/15 text-zinc-400',
      )}
      title={PACKAGE_SOURCE_TITLE[source]}
    >
      {PACKAGE_SOURCE_LABEL[source]}
    </Badge>
  );
}

/** Uploaded here vs inherited from the script-built catalog. */
export function StickerSourceBadge({ source }: { source: StickerSource }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        base,
        source === 'MANAGED' ? 'bg-blue-400/15 text-blue-400' : 'bg-zinc-400/15 text-zinc-400',
      )}
      title={
        source === 'MANAGED'
          ? 'Uploaded through this portal'
          : 'Inherited from the offline sticker script; delisting adopts it for curation'
      }
    >
      {source === 'MANAGED' ? 'Uploaded' : 'Inherited'}
    </Badge>
  );
}

/** An animated loop: the thumbnail stays a still, the original plays only on request. */
export function AnimatedStickerBadge({ sticker }: { sticker: StickerAnimationFacts }) {
  return (
    <Badge
      variant="outline"
      className={cn(base, 'bg-violet-400/15 text-violet-400')}
      title={
        sticker.frameCount
          ? `${sticker.frameCount} frames, loops forever. The thumbnail shows frame 1.`
          : 'Animated sticker. The thumbnail shows frame 1.'
      }
    >
      {animatedBadgeLabel(sticker)}
    </Badge>
  );
}
